import type { DeviceInfo } from '../stores';
import type { DriverState } from '@playcastdotio/CoreTypes';

/** Matches PlayJector driver_status keys for plugged Windows slots (ShadowCam00, ShadowCam100, …). */
const SHADOWCAM_SLOT_KEY_PATTERN = /^ShadowCam\d+$/;

type ShadowcamDriverState = DriverState | Record<string, boolean> | undefined;

/** Plugged Windows slot names from PlayJector driver_status (`true` ShadowCamNN keys only). */
export function getPluggedShadowcamSlotKeys(
  driverState: ShadowcamDriverState,
): string[] {
  if (!driverState) {
    return [];
  }
  return Object.entries(driverState)
    .filter(([key, value]) => SHADOWCAM_SLOT_KEY_PATTERN.test(key) && value === true)
    .map(([key]) => key)
    .sort((a, b) => {
      const na = parseInt(a.replace('ShadowCam', ''), 10);
      const nb = parseInt(b.replace('ShadowCam', ''), 10);
      return na - nb;
    });
}

function findShadowcamByLabel(shadowcams: DeviceInfo[], labelSubstring: string): DeviceInfo | undefined {
  return shadowcams.find((sc) => sc.displayName.includes(labelSubstring));
}

/**
 * Map a PlayJector hub route (e.g. ShadowCam03) to a Chromium-enumerated device.
 *
 * Hub routes and Windows pool slot names can decouple under the dynamic pool: plugging
 * ShadowCam03 may enumerate as ShadowCam00. Resolution order:
 * 1. Direct label match on hub route (legacy contiguous routes).
 * 2. Sole plugged driver slot equals the hub route → that sole enumerated device
 *    (Chromium may use a different label than the hub route).
 * 3. Otherwise null — never map hubRoute ShadowCam02 onto an unrelated ShadowCam00
 *    just because it is the only device present (that produced green-screen joins).
 */
export function resolveShadowcamDevice(
  hubRoute: string,
  shadowcams: DeviceInfo[],
  driverState: ShadowcamDriverState,
): DeviceInfo | null {
  if (shadowcams.length === 0) {
    return null;
  }

  const direct = findShadowcamByLabel(shadowcams, hubRoute);
  if (direct) {
    return direct;
  }

  const pluggedSlots = getPluggedShadowcamSlotKeys(driverState);
  if (pluggedSlots.length === 1 && pluggedSlots[0] === hubRoute) {
    // Dynamic-pool remap: driver plugged this hub slot; Chromium may label it differently.
    if (shadowcams.length === 1) {
      return shadowcams[0];
    }
    return findShadowcamByLabel(shadowcams, pluggedSlots[0]) ?? null;
  }

  // Multiple (or mismatched) plugged slots: only a direct label match is safe.
  return null;
}

export async function waitForShadowcamDevice(
  hubRoute: string,
  enumerateShadowcams: () => Promise<DeviceInfo[]>,
  getDriverState: () => ShadowcamDriverState,
  timeoutMs = 8_000,
  pollMs = 100,
): Promise<DeviceInfo | null> {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const shadowcams = await enumerateShadowcams();
    const resolved = resolveShadowcamDevice(hubRoute, shadowcams, getDriverState());
    if (resolved) {
      return resolved;
    }
    await new Promise<void>((resolve) => setTimeout(resolve, pollMs));
  }

  const shadowcams = await enumerateShadowcams();
  return resolveShadowcamDevice(hubRoute, shadowcams, getDriverState());
}
